import { useEffect, useMemo, useRef, useState } from "react";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Banknote, LockKeyhole, PackageCheck, Plus, Trash2, TriangleAlert, Truck, Users, Search, CheckCircle2, ShoppingCart,
  Receipt, Undo2, BarChart3, Building2, Pencil
} from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { featureAllowed, screenAllowed } from "@/lib/access";
import { usePurchasing } from "@/hooks/usePurchasing";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageShell } from "@/components/PageShell";
import { MONEY_CAPTION, MONEY_GRAND_TOTAL, MONEY_LINE, inr as formatInr } from "@/lib/format";
import { EmptyState, LoadingState } from "@/components/ui/StateBoundaries";
import BillFiles, { uploadBillFiles, MAX_FILE_BYTES } from "@/components/purchase/BillFiles";
import ReturnDialog from "@/components/purchase/ReturnDialog";
import SupplierLedger from "@/components/purchase/SupplierLedger";
import PurchaseReports from "@/components/purchase/PurchaseReports";
import Departments from "@/components/purchase/Departments";
import ItemSearch from "@/components/purchase/ItemSearch";
import VendorItems from "@/components/purchase/VendorItems";
import * as purchaseApi from "@/lib/purchaseApi";

const PURCHASE_ROLES = ["admin"];

const PO_STATUSES = [
  ["", "All statuses"],
  ["draft", "Draft"],
  ["placed", "Placed"],
  ["partially_received", "Partially received"],
  ["received", "Received"],
  ["cancelled", "Cancelled"],
];

const PAYMENT_MODES = [
  ["cash", "Cash"],
  ["upi", "UPI"],
  ["bank_transfer", "Bank transfer"],
  ["cheque", "Cheque"],
  ["card", "Card"],
];

/**
 * Money here, with the em dash the shared formatter cannot know to keep.
 *
 * The local formatter this replaces hardcoded `₹` and en-IN, so a restaurant that
 * had configured another currency symbol saw it everywhere except this page (§4.2).
 * A supplier line with no cost on file is unknown, not zero (§47) — that is the only
 * branch that survives; the rest is inr().
 */
const inr = value =>
  value === null || value === undefined || value === "" ? "—" : formatInr(value);

const qty = value => {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount)
    ? amount.toLocaleString("en-IN", { maximumFractionDigits: 3 })
    : "—";
};

const label = value => String(value || "").replace(/_/g, " ");
const day = value => (value ? String(value).slice(0, 10) : "—");
const initials = name => String(name || "?").slice(0, 2).toUpperCase();
const outstandingFor = line => Math.max(0, Number(line.quantity_ordered) - Number(line.quantity_received));

// ---------------------------------------------------------------- dialogs

function SupplierDialog({ supplier, onSave, onClose }) {
  const [form, setForm] = useState({
    name: supplier?.name || "",
    phone: supplier?.phone || "",
    email: supplier?.email || "",
    gstin: supplier?.gstin || "",
    address: supplier?.address || "",
    payment_terms_days: String(supplier?.payment_terms_days ?? 0),
    is_active: supplier ? supplier.is_active !== false : true,
    // What was owed before RestoYaar (owner brief B12): the ledger starts from it.
    opening_balance: String(supplier?.opening_balance ?? 0),
    opening_date: supplier?.opening_date ? day(supplier.opening_date) : "",
  });
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Supplier name is required");
    const terms = Number(form.payment_terms_days);
    if (!Number.isInteger(terms) || terms < 0) return toast.error("Payment terms must be a whole number of days");
    const opening = Number(form.opening_balance || 0);
    if (!Number.isFinite(opening) || opening < 0) return toast.error("The opening balance cannot be negative");
    setSaving(true);
    const result = await onSave({
      name: form.name.trim(),
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      gstin: form.gstin.trim() || null,
      address: form.address.trim() || null,
      payment_terms_days: terms,
      opening_balance: opening,
      opening_date: form.opening_date || null,
      ...(supplier ? { is_active: form.is_active } : {}),
    });
    setSaving(false);
    if (!result.ok) {
      setFailure(result.error);
      return toast.error(result.error);
    }
    toast.success(`${form.name.trim()} saved`);
    onClose();
  };

  return <Modal title={supplier ? `Edit ${supplier.name}` : "Add supplier"} onClose={onClose} testId="supplier-modal" footer={<><Button variant="outline" onClick={onClose} data-testid="save-supplier-button-cancel">Cancel</Button><Button variant="action" onClick={submit} disabled={saving} data-testid="save-supplier-button">{supplier ? "Save changes" : "Add supplier"}</Button></>}>
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="text-meta font-medium text-muted-foreground">Name</label>
          <Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="supplier-name-input"/>
        </div>
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="text-meta font-medium text-muted-foreground">Phone</label>
          <Input value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="supplier-phone-input"/>
        </div>
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="text-meta font-medium text-muted-foreground">Email</label>
          <Input value={form.email} onChange={e => set("email", e.target.value)} data-testid="supplier-email-input"/>
        </div>
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="text-meta font-medium text-muted-foreground">GSTIN</label>
          <Input value={form.gstin} onChange={e => set("gstin", e.target.value)} data-testid="supplier-gstin-input"/>
        </div>
        <div className="space-y-1.5 col-span-2">
          <label className="text-meta font-medium text-muted-foreground">Address</label>
          <textarea className="w-full min-h-[80px] p-3 text-body rounded-md border bg-card focus:outline-none focus:ring-2 focus:ring-primary/50" rows={2} value={form.address} onChange={e => set("address", e.target.value)} data-testid="supplier-address-input"/>
        </div>
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="text-meta font-medium text-muted-foreground">Payment terms (days)</label>
          <Input type="number" min="0" step="1" value={form.payment_terms_days} onChange={e => set("payment_terms_days", e.target.value)} data-testid="supplier-terms-input"/>
        </div>
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="text-meta font-medium text-muted-foreground">Owed before RestoYaar (₹)</label>
          <Input type="number" min="0" step="0.01" value={form.opening_balance} onFocus={e => e.target.select()}
                 onChange={e => set("opening_balance", e.target.value)} data-testid="supplier-opening-input"/>
        </div>
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="block text-meta font-medium text-muted-foreground">As of</label>
          <DatePicker value={form.opening_date} onChange={v => set("opening_date", v)} ariaLabel="Opening balance date" testId="supplier-opening-date"/>
        </div>
      </div>
      {supplier && (
        <label className="flex items-center gap-2 text-body font-semibold mt-4">
          <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary" checked={form.is_active} onChange={e => set("is_active", e.target.checked)} data-testid="supplier-active-checkbox"/>
          Active
        </label>
      )}
      {failure && <p className="text-body font-semibold text-destructive mt-2" data-testid="supplier-error">{failure}</p>}
    </div>
  </Modal>;
}

/**
 * One form for three jobs (2026-11-01): a new purchase order, a supplier bill entered
 * as bought ("bill": invoice no./date, paid now, bill photo), or a draft being edited.
 */
function OrderDialog({ mode = "order", order = null, suppliers, stockItems, departments = [], sources = [], initialPaidFrom = "", onSave, onUpload, onClose }) {
  const isBill = mode === "bill";
  const activeDepts = departments.filter(d => d.is_active || d.id === order?.department_id);
  const [supplierId, setSupplierId] = useState(order ? String(order.supplier_id) : "");
  const [departmentId, setDepartmentId] = useState(order?.department_id ? String(order.department_id)
    : activeDepts.length === 1 ? String(activeDepts[0].id) : "");
  const [orderDate, setOrderDate] = useState(order ? day(order.order_date) : "");
  const [expectedDate, setExpectedDate] = useState(order?.expected_date ? day(order.expected_date) : "");
  const [invoiceNumber, setInvoiceNumber] = useState(order?.invoice_number || "");
  const [invoiceDate, setInvoiceDate] = useState(order?.invoice_date ? day(order.invoice_date) : (isBill ? new Date().toLocaleDateString("en-CA") : ""));
  const [notes, setNotes] = useState(order?.notes || "");
  const [paidAmount, setPaidAmount] = useState("");
  const [paidMode, setPaidMode] = useState("cash");
  const pickSource = list => (initialPaidFrom && list.includes(initialPaidFrom) ? initialPaidFrom : list[0] || "");
  const [paidFrom, setPaidFrom] = useState(pickSource(sources));
  // The outlet's sources arrive after the dialog opens: fill the choice once they do.
  useEffect(() => { if (!paidFrom && sources.length) setPaidFrom(pickSource(sources)); }, [sources]); // eslint-disable-line react-hooks/exhaustive-deps
  const [files, setFiles] = useState([]);
  const filesInput = useRef(null);
  const [lines, setLines] = useState(order
    ? order.items.map(line => ({ stockItemId: String(line.stock_item_id), quantity: String(line.quantity_ordered),
                                 unitCost: String(line.unit_cost), taxPercent: String(line.tax_percent), buyUnitId: "" }))
    : []);
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);
  const { token } = usePOS();
  const itemById = useMemo(() => Object.fromEntries(stockItems.map(i => [String(i.id), i])), [stockItems]);
  // How suppliers sell each item on the bill (owner brief §3.2): "2 BTL 750" brings in
  // 1,500 ML. Read once per item; a line starts in the unit it was last bought in, or
  // the item's default buy unit.
  const [buyUnits, setBuyUnits] = useState({});
  const lineIds = lines.map(l => l.stockItemId).join(",");
  useEffect(() => {
    const missing = [...new Set(lines.map(l => l.stockItemId))].filter(id => id && !(id in buyUnits));
    if (!missing.length) return;
    missing.forEach(id => {
      purchaseApi.listBuyUnits(Number(id), token)
        .then(units => {
          setBuyUnits(m => ({ ...m, [id]: units }));
          const fallback = units.find(u => u.is_default);
          if (fallback) setLines(cur => cur.map(l => (l.stockItemId === id && l.buyUnitId === undefined
            ? { ...l, buyUnitId: String(fallback.id) } : l)));
        })
        .catch(() => setBuyUnits(m => ({ ...m, [id]: [] })));
    });
  }, [lineIds]); // eslint-disable-line react-hooks/exhaustive-deps
  // Owner 2026-11-01: picking the vendor lists everything they supply at the last rate
  // and quantity 0; fill in what came, and anything left at 0 is not on this bill.
  const [vendorItems, setVendorItems] = useState({});
  const [vendorState, setVendorState] = useState(null);
  useEffect(() => {
    if (order || !supplierId) return undefined;
    let stale = false;
    setVendorState("loading");
    purchaseApi.listSupplierItems(Number(supplierId), token)
      .then(list => {
        if (stale) return;
        setVendorItems(Object.fromEntries(list.map(i => [String(i.stock_item_id), i])));
        setLines(current => {
          // Quantities already typed for items still listed are kept.
          const typed = Object.fromEntries(current.filter(l => l.quantity !== "").map(l => [l.stockItemId, l]));
          const fromVendor = list.map(i => typed[String(i.stock_item_id)] || {
            stockItemId: String(i.stock_item_id), quantity: "",
            unitCost: i.last_unit_cost == null ? "" : String(i.last_unit_cost),
            taxPercent: String(i.last_tax_percent ?? 0),
            // The vendor's rate is per the unit it was bought in (review M2).
            ...(i.last_buy_unit_id ? { buyUnitId: String(i.last_buy_unit_id) } : {}) });
          const listed = new Set(list.map(i => String(i.stock_item_id)));
          return [...fromVendor, ...current.filter(l => l.quantity !== "" && !listed.has(l.stockItemId))];
        });
        setVendorState(list.length ? "loaded" : "empty");
      })
      .catch(err => { if (!stale) setVendorState(`failed: ${err?.response?.data?.detail || err?.message || "no answer"}`); });
    return () => { stale = true; };
  }, [supplierId, order, token]);

  const setLine = (index, key, value) =>
    setLines(current => current.map((line, i) => (i === index ? { ...line, [key]: value } : line)));
  const addItem = item => {
    const known = vendorItems[String(item.id)];
    setLines(current => [...current, { stockItemId: String(item.id), quantity: "",
      unitCost: known?.last_unit_cost == null ? "" : String(known.last_unit_cost),
      taxPercent: String(known?.last_tax_percent ?? 0),
      ...(known?.last_buy_unit_id ? { buyUnitId: String(known.last_buy_unit_id) } : {}) }]);
  };
  const filled = lines.filter(l => Number(l.quantity) > 0).length;
  // What the bill comes to, worked the way the server works it (purchase_service:
  // every line unrounded, then subtotal and tax rounded once), so it can be checked
  // against the paper bill before saving (owner 2026-11-01: "I got confused what
  // goes where"). The server's figure is still the one saved.
  const lineMoney = line => {
    const qty = Number(line.quantity) || 0;
    const rate = Number(line.unitCost) || 0;
    const net = qty * rate;
    return { net, tax: net * (Number(line.taxPercent) || 0) / 100 };
  };
  const totals = lines.reduce((t, line) => {
    const m = lineMoney(line);
    return { net: t.net + m.net, tax: t.tax + m.tax };
  }, { net: 0, tax: 0 });
  const round2 = v => Math.round((v + Number.EPSILON) * 100) / 100;
  const billSubtotal = round2(totals.net);
  const billTax = round2(totals.tax);
  const billTotal = round2(billSubtotal + billTax);
  const paidNow = Number(paidAmount) || 0;
  // A box that starts at 0 must not turn a typed 5 into "05" (B10).
  const selectOnFocus = e => e.target.select();

  const submit = async () => {
    if (!supplierId) return toast.error("Choose a supplier");
    const items = [];
    for (const line of lines) {
      if (!line.stockItemId) continue;
      const quantity = Number(line.quantity || 0);
      // Left at 0 (or empty): this item did not come on this bill.
      if (quantity === 0) continue;
      const name = itemById[line.stockItemId]?.name || "An item";
      const unitCost = Number(line.unitCost);
      if (!Number.isFinite(quantity) || quantity < 0) return toast.error(`${name}: the quantity must be a number above zero`);
      if (line.unitCost === "" || !Number.isFinite(unitCost) || unitCost < 0) return toast.error(`${name}: enter the rate`);
      items.push({
        stock_item_id: Number(line.stockItemId),
        quantity_ordered: quantity,
        unit_cost: unitCost,
        tax_percent: Number(line.taxPercent) || 0,
        ...(line.buyUnitId ? { buy_unit_id: Number(line.buyUnitId) } : {}),
      });
    }
    if (!items.length) return toast.error("Enter the quantity of at least one item");
    if (departments.length && !departmentId) return toast.error("Choose the department this purchase is for");
    const paid = paidAmount === "" ? null : Number(paidAmount);
    if (paid !== null && (!Number.isFinite(paid) || paid <= 0)) return toast.error("Paid now must be above zero, or empty");

    const body = {
      supplier_id: Number(supplierId),
      items,
      department_id: departmentId ? Number(departmentId) : null,
      invoice_number: invoiceNumber.trim() || null,
      invoice_date: invoiceDate || null,
      notes: notes.trim() || null,
      ...(isBill ? (paid ? { paid_amount: paid, paid_mode: paidMode, ...(paidFrom ? { paid_from: paidFrom } : {}) } : {})
        : { order_date: orderDate || null, expected_date: expectedDate || null }),
    };
    setSaving(true);
    const result = await onSave(body);
    if (!result.ok) {
      setSaving(false);
      setFailure(result.error);
      return toast.error(result.error);
    }
    const attached = files.length ? await uploadBillFiles(files, file => onUpload({ orderId: result.data.id }, file)) : 0;
    setSaving(false);
    const filesText = files.length ? ` · ${attached} of ${files.length} file${files.length === 1 ? "" : "s"} attached` : "";
    if (attached < files.length) {
      // The record is saved; the files that failed were named one by one already.
      toast.error(`${result.data.po_number} saved, but ${files.length - attached} file(s) did not attach — add them from the purchase`);
    }
    toast.success(isBill ? `${result.data.po_number} entered · stock in · ${inr(result.data.total)}${filesText}`
      : order ? `${result.data.po_number} saved · ${inr(result.data.total)}`
        : `${result.data.po_number} created as a draft · ${inr(result.data.total)}${filesText}`);
    onClose();
  };

  const title = isBill ? "Enter a supplier bill" : order ? `Edit ${order.po_number}` : "New purchase order";
  const saveText = isBill ? "Save bill · stock in" : order ? "Save changes" : "Create draft";
  return <Modal title={title} size="wide" onClose={onClose} testId="purchase-order-modal" footer={<><Button variant="outline" onClick={onClose} data-testid="save-purchase-order-button-cancel">Cancel</Button><Button variant="action" onClick={submit} disabled={saving} data-testid="save-purchase-order-button">{saving ? "Saving…" : saveText}</Button></>}>
    <div className="space-y-6">
      <h3 className="text-body font-semibold -mb-4" data-testid="po-section-details">1 · {isBill ? "Bill details" : "Order details"}</h3>
      <div className="grid grid-cols-2 gap-4 bg-muted/30 p-4 rounded-xl border">
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="text-meta font-medium text-muted-foreground">Supplier</label>
          <select className="w-full h-9 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={supplierId} onChange={e => setSupplierId(e.target.value)} data-testid="po-supplier-select">
            <option value="">Choose…</option>
            {suppliers.filter(s => s.is_active !== false).map(s => <option value={s.id} key={s.id}>{s.name}</option>)}
          </select>
        </div>
        {departments.length > 0 && (
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <label className="text-meta font-medium text-muted-foreground">Department</label>
            <select className="w-full h-9 px-3 border rounded-md bg-card text-body" value={departmentId}
                    onChange={e => setDepartmentId(e.target.value)} data-testid="po-department-select">
              <option value="">Choose…</option>
              {activeDepts.map(d => <option value={d.id} key={d.id}>{d.name}</option>)}
            </select>
          </div>
        )}
        {isBill ? <>
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <label className="text-meta font-medium text-muted-foreground">Supplier's bill no.</label>
            <Input value={invoiceNumber} maxLength={60} onChange={e => setInvoiceNumber(e.target.value)} data-testid="po-invoice-number-input"/>
          </div>
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <label className="block text-meta font-medium text-muted-foreground">Bill date</label>
            <DatePicker value={invoiceDate} onChange={setInvoiceDate} ariaLabel="Bill date" testId="po-invoice-date-input"/>
          </div>
        </> : <>
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <label className="block text-meta font-medium text-muted-foreground">Order date</label>
            <DatePicker value={orderDate} onChange={setOrderDate} ariaLabel="Order date" testId="po-order-date-input"/>
          </div>
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <label className="block text-meta font-medium text-muted-foreground">Expected</label>
            <DatePicker value={expectedDate} min={orderDate || undefined} onChange={setExpectedDate} ariaLabel="Expected date" testId="po-expected-date-input"/>
          </div>
        </>}
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="text-meta font-medium text-muted-foreground">Notes</label>
          <Input value={notes} onChange={e => setNotes(e.target.value)} data-testid="po-notes-input"/>
        </div>
      </div>

      <h3 className="text-body font-semibold -mb-4" data-testid="po-section-items">2 · Items {isBill ? "on the bill" : "to order"}</h3>
      <div className="space-y-2" data-testid="po-lines">
        {vendorState === "loading" && <p className="text-meta text-muted-foreground">Reading this vendor's items…</p>}
        {vendorState?.startsWith("failed") && (
          <p className="text-meta text-destructive" role="alert" data-testid="po-vendor-items-error">
            This vendor's items could not be read ({vendorState.slice(8)}) — add items by searching.
          </p>
        )}
        {vendorState === "empty" && (
          <p className="text-meta text-muted-foreground" data-testid="po-vendor-items-empty">
            No items saved for this vendor yet — search to add them; they will come up next time.
          </p>
        )}
        {lines.length > 0 && (
          <>
            <p className="text-meta text-muted-foreground px-1" data-testid="po-lines-filled">
              {filled} of {lines.length} in this bill · leave the quantity empty or 0 if it did not come ·
              rate is per unit, before GST
            </p>
            {/* Column headings; on a phone each box carries its own label instead. */}
            <div className="hidden sm:grid grid-cols-12 gap-2 px-2 text-micro font-medium uppercase tracking-wide text-muted-foreground" data-testid="po-lines-header">
              <span className="col-span-3">Item</span>
              <span className="col-span-2">Quantity</span>
              <span className="col-span-2">Rate ₹</span>
              <span className="col-span-2">GST %</span>
              <span className="col-span-2 text-right">Line total ₹</span>
              <span className="col-span-1" />
            </div>
          </>
        )}
        {lines.map((line, index) => {
          const item = itemById[line.stockItemId];
          const inBill = Number(line.quantity) > 0;
          return (
            <div key={`${line.stockItemId}-${index}`}
                 className={`grid grid-cols-12 gap-2 items-center rounded-lg border p-2 ${inBill ? "bg-card border-primary/40" : "bg-muted/30"}`}
                 data-testid={`po-line-${index}`}>
              <div className="col-span-12 sm:col-span-3 min-w-0">
                <span className="block text-body font-semibold truncate" data-testid={`po-line-${index}-name`}>{item?.name || `Item #${line.stockItemId}`}</span>
                {(buyUnits[line.stockItemId] || []).length > 0 ? (
                  <select className="h-9 px-tight border rounded-md bg-card text-meta" value={line.buyUnitId || ""}
                          onChange={e => setLine(index, "buyUnitId", e.target.value)} data-testid={`po-line-${index}-unit`}>
                    <option value="">per {item?.unit || "unit"}</option>
                    {buyUnits[line.stockItemId].map(u => (
                      <option key={u.id} value={u.id}>per {u.name} ({u.factor} {item?.unit})</option>
                    ))}
                  </select>
                ) : <span className="text-micro text-muted-foreground">per {item?.unit || "unit"}</span>}
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="sm:hidden text-micro text-muted-foreground block">Qty ({item?.unit || "unit"})</label>
                <Input type="number" inputMode="decimal" min="0" step="0.001" placeholder="0" className="h-11" onFocus={selectOnFocus}
                       value={line.quantity} onChange={e => setLine(index, "quantity", e.target.value)} data-testid={`po-line-${index}-quantity-input`}/>
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="sm:hidden text-micro text-muted-foreground block">Rate ₹ before tax</label>
                <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" className="h-11" onFocus={selectOnFocus}
                       value={line.unitCost} onChange={e => setLine(index, "unitCost", e.target.value)} data-testid={`po-line-${index}-unit-cost-input`}/>
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="sm:hidden text-micro text-muted-foreground block">GST %</label>
                <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder="0" className="h-11" onFocus={selectOnFocus}
                       value={line.taxPercent} onChange={e => setLine(index, "taxPercent", e.target.value)} data-testid={`po-line-${index}-tax-input`}/>
              </div>
              <div className="col-span-10 sm:col-span-2 text-right">
                <label className="sm:hidden text-micro text-muted-foreground">Line total </label>
                <span className="text-body font-semibold tabular-nums" data-testid={`po-line-${index}-total`}>
                  {inBill ? inr(round2(lineMoney(line).net + lineMoney(line).tax)) : "—"}
                </span>
              </div>
              <div className="col-span-2 sm:col-span-1 flex justify-end">
                <Button variant="ghost" size="icon" className="h-11 w-11 text-muted-foreground hover:text-destructive"
                        onClick={() => setLines(current => current.filter((_, i) => i !== index))}
                        aria-label={`Remove ${item?.name || "line"}`} data-testid={`po-line-${index}-remove-button`}>
                  <Trash2 className="w-5 h-5"/>
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <ItemSearch items={stockItems} exclude={lines.map(l => l.stockItemId)} onPick={addItem}
                  placeholder="Search an item to add (it joins this vendor's list)" testId="po-item-search" />

      <div className="rounded-xl border p-4 space-y-1 text-body" data-testid="po-totals">
        <div className="flex justify-between"><span className="text-muted-foreground">Items before tax</span><span className="tabular-nums" data-testid="po-subtotal">{inr(billSubtotal)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span className="tabular-nums" data-testid="po-tax">{inr(billTax)}</span></div>
        <div className="flex justify-between text-lede font-semibold border-t pt-1"><span>{isBill ? "Bill total" : "Order total"}</span><span className="tabular-nums" data-testid="po-total">{inr(billTotal)}</span></div>
        {isBill && paidNow > 0 && (
          <div className="flex justify-between text-meta text-muted-foreground" data-testid="po-credit">
            <span>Paid now {inr(paidNow)} · left on credit</span><span className="tabular-nums">{inr(round2(billTotal - paidNow))}</span>
          </div>
        )}
        <p className="text-micro text-muted-foreground">Check this against the paper bill. The server works the same sum and its total is the one saved.</p>
      </div>

      {isBill && <h3 className="text-body font-semibold -mb-4" data-testid="po-section-payment">3 · Payment</h3>}
      {isBill && (
        <div className="grid grid-cols-2 gap-4 bg-muted/30 p-4 rounded-xl border" data-testid="po-paid-now">
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <label className="text-meta font-medium text-muted-foreground">Paid now (leave empty if on credit)</label>
            <Input type="number" min="0" step="0.01" value={paidAmount} onFocus={selectOnFocus} onChange={e => setPaidAmount(e.target.value)} data-testid="po-paid-amount-input"/>
          </div>
          <div className="space-y-1.5 col-span-2 sm:col-span-1">
            <label className="text-meta font-medium text-muted-foreground">Paid by</label>
            <select className="w-full h-9 px-3 border rounded-md bg-card text-body" value={paidMode} onChange={e => setPaidMode(e.target.value)} data-testid="po-paid-mode-select">
              {PAYMENT_MODES.map(([value, text]) => <option value={value} key={value}>{text}</option>)}
            </select>
          </div>
          {sources.length > 0 && (
            <div className="space-y-1.5 col-span-2">
              <label className="text-meta font-medium text-muted-foreground">Paid from</label>
              <select className="w-full h-11 px-3 border rounded-md bg-card text-body" value={paidFrom}
                      onChange={e => setPaidFrom(e.target.value)} data-testid="po-paid-from-select">
                {sources.map(src => <option key={src} value={src}>{src}</option>)}
              </select>
            </div>
          )}
        </div>
      )}
      {!order && <h3 className="text-body font-semibold -mb-4" data-testid="po-section-photo">{isBill ? 4 : 3} · Bill photo</h3>}
      {!order && (
        <div className="space-y-1.5">
          <label className="text-meta font-medium text-muted-foreground">Bill / invoice photo or PDF</label>
          {/* A full-size button, not the browser's tiny "Choose Files" (owner 2026-11-01:
              "on phone the choose file button is not working"). */}
          <input ref={filesInput} type="file" accept="image/*,application/pdf" multiple className="hidden"
                 onChange={e => { setFiles(f => [...f, ...Array.from(e.target.files || [])]); e.target.value = ""; }}
                 data-testid="po-files-input"/>
          <Button type="button" variant="outline" className="w-full h-11 border-dashed" onClick={() => filesInput.current?.click()}
                  data-testid="po-files-button">
            <Receipt className="w-4 h-4 mr-2"/>{files.length ? "Add another photo or PDF" : "Photograph or choose the bill"}
          </Button>
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="flex items-center gap-snug text-meta" data-testid={`po-file-${i}`}>
              <span className="flex-1 truncate">{f.name}</span>
              <Button type="button" variant="ghost" size="icon" aria-label={`Remove ${f.name}`}
                      onClick={() => setFiles(list => list.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4"/></Button>
            </div>
          ))}
          {files.some(f => f.type === "application/pdf" && f.size > MAX_FILE_BYTES) && (
            <p className="text-meta text-destructive">A PDF over 4 MB will not attach — photos are shrunk automatically.</p>
          )}
        </div>
      )}

      <div className="bg-info-subtle border border-info/20 p-3 rounded-lg text-meta text-info">
        {isBill ? "Saving brings the stock in at these costs at once." : "The order is saved as a draft; nothing comes into stock until it is received."}
      </div>
      {failure && <p className="text-body font-semibold text-destructive mt-2" data-testid="po-create-error">{failure}</p>}
    </div>
  </Modal>;
}

function ReceiveDialog({ order, onReceive, onClose }) {
  const [quantities, setQuantities] = useState(() =>
    Object.fromEntries(order.items.map(line => [line.id, String(outstandingFor(line) || "")]))
  );
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const lines = [];
    for (const line of order.items) {
      const value = Number(quantities[line.id]);
      if (!quantities[line.id] || value === 0) continue;
      if (!Number.isFinite(value) || value < 0) return toast.error("Received quantity cannot be negative");
      lines.push({ purchase_order_item_id: line.id, quantity: value });
    }
    if (!lines.length) return toast.error("Enter what actually arrived on at least one line");

    setSaving(true);
    const result = await onReceive(order.id, lines, order.version);
    setSaving(false);
    if (!result.ok) {
      setFailure(result.error);
      return toast.error(result.error);
    }
    toast.success(`${result.data.po_number} is now ${label(result.data.status)}`);
    onClose();
  };

  return <Modal title={`Receive ${order.po_number}`} onClose={onClose} testId="receive-modal" footer={<><Button variant="outline" onClick={onClose} data-testid="confirm-receive-button-cancel">Cancel</Button><Button variant="action" onClick={submit} disabled={saving} data-testid="confirm-receive-button">Book delivery</Button></>}>
    <div className="space-y-4">
      <p className="text-body text-muted-foreground font-medium">
        Enter what actually arrived. Receiving is what moves stock — a purchase movement
        is written per line at the ordered unit cost.
      </p>
      
      <div className="space-y-2" data-testid="receive-lines">
        <div className="hidden sm:grid grid-cols-12 gap-2 text-meta font-medium text-muted-foreground px-2">
          <span className="col-span-4">Item</span>
          <span className="col-span-2 text-right">Ordered</span>
          <span className="col-span-2 text-right">Received</span>
          <span className="col-span-4 pl-4">Arriving now</span>
        </div>
        
        {order.items.map((line, index) => {
          const outstanding = outstandingFor(line);
          const isDone = outstanding <= 0;
          return (
            <div key={line.id} className={`grid grid-cols-12 gap-2 items-center p-3 rounded-lg border ${isDone ? 'bg-muted/50 border-transparent opacity-70' : 'bg-card'}`} data-testid={`receive-line-${index}`}>
              <div className="col-span-12 sm:col-span-4 font-semibold text-body">{line.item_name}</div>
              <div className="col-span-4 sm:col-span-2 text-meta sm:text-right">
                <span className="sm:hidden text-meta font-medium text-muted-foreground mr-1">Ordered:</span>
                {qty(line.quantity_ordered)} <span className="opacity-70">{line.unit}</span>
              </div>
              <div className="col-span-4 sm:col-span-2 text-meta sm:text-right">
                <span className="sm:hidden text-meta font-medium text-muted-foreground mr-1">Received:</span>
                {qty(line.quantity_received)} <span className="opacity-70">{line.unit}</span>
              </div>
              <div className="col-span-4 sm:col-span-4 sm:pl-4">
                <Input
                  type="number"
                  min="0"
                  step="0.001"
                  className={isDone ? "bg-muted" : ""}
                  value={quantities[line.id] ?? ""}
                  onChange={e => setQuantities(current => ({ ...current, [line.id]: e.target.value }))}
                  disabled={isDone}
                  aria-label={`Quantity received for ${line.item_name}`}
                  data-testid={`receive-line-${index}-quantity-input`}
                />
              </div>
            </div>
          )
        })}
      </div>
      {failure && <p className="text-body font-semibold text-destructive mt-2" data-testid="receive-error">{failure}</p>}
    </div>
  </Modal>;
}

function PaymentDialog({ suppliers, orders, sources = [], presetSupplierId, onSave, onClose }) {
  const [supplierId, setSupplierId] = useState(presetSupplierId ? String(presetSupplierId) : "");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  // Which purse paid (owner brief §3.4): drawer and home cash reconcile apart.
  const [paidFrom, setPaidFrom] = useState(sources[0] || "");
  // Set when the server said this is more than is owed (B4): ask, never assume.
  const [advance, setAdvance] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [paidOn, setPaidOn] = useState("");
  const [notes, setNotes] = useState("");
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);

  const supplierOrders = orders.filter(order => String(order.supplier_id) === String(supplierId));

  const submit = async (asAdvance = false) => {
    if (!supplierId) return toast.error("Choose a supplier");
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) return toast.error("Enter an amount greater than zero");
    setSaving(true);
    const result = await onSave({
      supplier_id: Number(supplierId),
      amount: value,
      purchase_order_id: orderId ? Number(orderId) : null,
      mode,
      paid_on: paidOn || null,
      notes: notes.trim() || null,
      paid_from: paidFrom || null,
      ...(asAdvance ? { is_advance: true } : {}),
    });
    setSaving(false);
    if (!result.ok) {
      if (result.detail?.advance_needed) {
        setAdvance(result.detail);
        return setFailure(null);
      }
      setFailure(result.error);
      return toast.error(result.error);
    }
    toast.success(`${inr(result.data.amount)} recorded`);
    onClose();
  };

  return <Modal title="Record supplier payment" onClose={onClose} testId="supplier-payment-modal" footer={<><Button variant="outline" onClick={onClose} data-testid="save-payment-button-cancel">Cancel</Button><Button variant="action" onClick={() => submit(false)} disabled={saving || !!advance} data-testid="save-payment-button">Record payment</Button></>}>
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5 col-span-2">
          <label className="text-meta font-medium text-muted-foreground">Supplier</label>
          <select className="w-full h-9 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={supplierId} onChange={e => { setSupplierId(e.target.value); setOrderId(""); }} data-testid="payment-supplier-select">
            <option value="">Choose…</option>
            {suppliers.map(s => <option value={s.id} key={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="text-meta font-medium text-muted-foreground">Amount</label>
          <Input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} data-testid="payment-amount-input"/>
        </div>
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="text-meta font-medium text-muted-foreground">Mode</label>
          <select className="w-full h-9 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={mode} onChange={e => setMode(e.target.value)} data-testid="payment-mode-select">
            {PAYMENT_MODES.map(([value, text]) => <option value={value} key={value}>{text}</option>)}
          </select>
        </div>
        {sources.length > 0 && (
          <div className="space-y-1.5 col-span-2">
            <label className="text-meta font-medium text-muted-foreground">Paid from</label>
            <select className="w-full h-11 px-3 border rounded-md bg-card text-body" value={paidFrom}
                    onChange={e => setPaidFrom(e.target.value)} data-testid="payment-from-select">
              {sources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className="space-y-1.5 col-span-2">
          <label className="text-meta font-medium text-muted-foreground">Against purchase order</label>
          <select className="w-full h-9 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={orderId} onChange={e => setOrderId(e.target.value)} data-testid="payment-order-select">
            <option value="">On account — no specific order</option>
            {supplierOrders.map(order => (
              <option value={order.id} key={order.id}>{order.po_number} · {inr(order.total)}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="block text-meta font-medium text-muted-foreground">Paid on</label>
          <DatePicker value={paidOn} onChange={setPaidOn} ariaLabel="Paid on" testId="payment-date-input"/>
        </div>
        <div className="space-y-1.5 col-span-2 sm:col-span-1">
          <label className="text-meta font-medium text-muted-foreground">Reference</label>
          <Input value={notes} onChange={e => setNotes(e.target.value)} data-testid="payment-reference-input"/>
        </div>
      </div>
      {advance && (
        <div className="rounded-lg border border-warning bg-warning-subtle p-base space-y-snug" role="alert" data-testid="payment-advance-prompt">
          <p className="text-body text-warning font-semibold">{advance.message}.</p>
          <p className="text-meta">Record {inr(Number(advance.amount))} as an advance? It stays on the supplier's account against future bills.</p>
          <div className="grid grid-cols-2 gap-snug">
            <Button variant="outline" className="h-11" onClick={() => setAdvance(null)} data-testid="payment-advance-no">No, change the amount</Button>
            <Button variant="action" className="h-11" disabled={saving} onClick={() => submit(true)} data-testid="payment-advance-yes">Yes, an advance</Button>
          </div>
        </div>
      )}
      {failure && <p className="text-body font-semibold text-destructive mt-2" data-testid="payment-error">{failure}</p>}
    </div>
  </Modal>;
}

// ---------------------------------------------------------------- page

export default function PurchasePage() {
  const { user, token, permissions, settings } = usePOS();
  // Owner 2026-11-01: "Purchase order won't work for us … we directly upload bill when
  // received." Off: bills only — no order step, filter or open-orders figure.
  const ordersOn = String(settings?.purchase_orders_enabled ?? "false").toLowerCase() === "true";
  // Per-person switches (2026-10-20): see vs create/place/receive/pay. An old
  // cached session falls back to the role list (lib/access.js).
  const canPurchase = screenAllowed("purchase", user, permissions, PURCHASE_ROLES);
  const canEdit = featureAllowed("purchase.edit", user, permissions, PURCHASE_ROLES.includes(user?.role));
  // Where supplier payments come from (owner brief §3.4), the outlet's own list.
  const [sources, setSources] = useState([]);
  useEffect(() => {
    if (!token) return undefined;
    let stale = false;
    purchaseApi.listPaymentSources(token).then(list => { if (!stale) setSources(list); }).catch(() => {});
    return () => { stale = true; };
  }, [token]);
  // A department buyer (Saket: Kitchen + Beverage) sees purchases, not supplier money.
  const deptLimited = (user?.role !== "admin") && (user?.purchase_department_ids || []).length > 0;
  const purchasing = usePurchasing(token, canPurchase, { money: !deptLimited });
  const [tab, setTab] = useState("orders");
  const [dialog, setDialog] = useState(null);
  // C5: the Register's "Walk-in purchase" opens the bill dialog with "Paid from" on the drawer.
  // Read once from the address, then dropped from it so a reload does not open it again.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("enter") !== "bill" || !canEdit) return;
    setDialog({ kind: "bill", paidFrom: q.get("paid_from") || "" });
    window.history.replaceState(null, "", window.location.pathname);
  }, [canEdit]);
  const [selectedId, setSelectedId] = useState(null);
  const [actionError, setActionError] = useState(null);

  const selected = purchasing.orders.find(order => order.id === selectedId) || null;

  const owed = useMemo(
    () =>
      Math.round(
        purchasing.payables.reduce((sum, row) => sum + Number(row.outstanding || 0), 0) * 100
      ) / 100,
    [purchasing.payables]
  );

  const openCount = purchasing.orders.filter(order =>
    ["draft", "placed", "partially_received"].includes(order.status)
  ).length;

  const act = async (fn, successText) => {
    setActionError(null);
    const result = await fn();
    if (!result.ok) {
      setActionError(result.error);
      return toast.error(result.error);
    }
    toast.success(`${result.data.po_number} is now ${label(result.data.status)}${successText ? ` · ${successText}` : ""}`);
  };

  if (!canPurchase) {
    return (
      <PageShell testId="purchase-supply-page">
        <EmptyState icon={LockKeyhole} title="Access Denied" message="Suppliers, purchase orders and payables need an admin account." testId="purchase-forbidden"/>
      </PageShell>
    );
  }

  const tabs = [
    ["orders", "Purchases", Truck],
    ["returns", "Returns", Undo2],
    ["suppliers", "Suppliers", Users],
    ["payables", "Payables", Banknote],
    ["reports", "Reports", BarChart3],
    ["departments", "Departments", Building2],
  ].filter(([id]) => !(deptLimited && id === "payables"));

  /*
   * The caption keeps the one sentence in the old hero that changes what a user
   * does: placing commits nothing, receiving is what moves stock. SUPPLY CHAIN and
   * "Purchase & Inwarding" were the topbar's word "Purchase" said twice more.
   */
  return <PageShell
    testId="purchase-supply-page"
    caption={ordersOn
      ? "A bill entered as bought brings stock in at once. A purchase order commits nothing until it is received."
      : "Enter the supplier's bill when the goods arrive — stock comes in at once, with the bill's photo."}
    actions={canEdit && <>
      <Button variant="outline" size="sm" className="font-semibold shadow-sm" onClick={() => setDialog({ kind: "supplier" })} data-testid="add-supplier-button">
        <Plus className="w-4 h-4 mr-snug"/>Add supplier
      </Button>
      {ordersOn && (
        <Button variant="outline" size="sm" className="font-semibold shadow-sm" onClick={() => setDialog({ kind: "order" })} data-testid="new-purchase-order-button">
          <Plus className="w-4 h-4 mr-snug"/>New purchase order
        </Button>
      )}
      <Button variant="action" size="sm" className="font-semibold shadow-sm" onClick={() => setDialog({ kind: "bill" })} data-testid="enter-bill-button">
        <Receipt className="w-4 h-4 mr-snug"/>Enter a bill
      </Button>
    </>}
  >
    {purchasing.error && (
      <div className="bg-destructive/10 text-destructive border border-destructive/20 p-base rounded-lg flex items-start gap-snug shrink-0" role="alert" data-testid="purchase-error">
        <TriangleAlert className="w-4 h-4 shrink-0 mt-hair"/>
        <span className="text-body font-semibold">{purchasing.error}</span>
      </div>
    )}

    {/* Four figures, one strip. Same shape as Inventory's, because they are the
        same kind of thing: a row of counts about one dataset (§2.2, §4.3). */}
    <div className="grid grid-cols-2 lg:grid-cols-4 border rounded-lg bg-card divide-x divide-y lg:divide-y-0 shrink-0" data-testid="purchase-metrics">
      {ordersOn && (
        <div className="p-base flex flex-col gap-hair">
          <small className="text-meta font-medium text-muted-foreground">Open orders</small>
          <strong className="text-lede font-semibold text-foreground tabular-nums" data-testid="metric-open-orders">{openCount}</strong>
          <b className="text-meta font-medium text-muted-foreground">draft, placed or part-received</b>
        </div>
      )}
      <div className="p-base flex flex-col gap-hair">
        <small className="text-meta font-medium text-muted-foreground">Suppliers</small>
        <strong className="text-lede font-semibold text-foreground tabular-nums" data-testid="metric-suppliers">{purchasing.suppliers.length}</strong>
        <b className="text-meta font-medium text-muted-foreground">active and inactive</b>
      </div>
      {!deptLimited && <>
      <div className="p-base flex flex-col gap-hair">
        <small className="text-meta font-medium text-muted-foreground">Outstanding</small>
        <strong className={`${MONEY_GRAND_TOTAL} ${owed > 0 ? 'text-warning' : 'text-foreground'}`} data-testid="metric-outstanding">{inr(owed)}</strong>
        <b className="text-meta font-medium text-muted-foreground">billed minus paid</b>
      </div>
      <div className="p-base flex flex-col gap-hair">
        <small className="text-meta font-medium text-muted-foreground">Payments recorded</small>
        <strong className="text-lede font-semibold text-foreground tabular-nums" data-testid="metric-payments">{purchasing.payments.length}</strong>
        <b className="text-meta font-medium text-muted-foreground">latest 100</b>
      </div>
      </>}
    </div>

    <div className="flex gap-snug overflow-x-auto scrollbar-hide border-b shrink-0" data-testid="module-tabs">
      {tabs.map(([id, text, Icon]) => {
        const isActive = tab === id;
        return (
          <button
            key={id}
            className={`flex shrink-0 items-center gap-snug whitespace-nowrap px-base py-snug -mb-px text-body font-medium transition-colors border-b-2 ${isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setTab(id)}
            data-testid={`purchase-tab-${id}-button`}
          >
            <Icon className="w-4 h-4" />
            {text}
          </button>
        )
      })}
    </div>

    {tab === "orders" && <div className="flex flex-col lg:flex-row gap-loose items-start min-w-0 w-full" data-testid="purchase-orders-panel">
      <section className="w-full lg:w-1/2 flex flex-col gap-base min-w-0">
        <header className="flex items-center justify-between gap-snug bg-muted/30 px-base py-snug rounded-lg border">
          <h2 className="text-title font-semibold text-foreground">Purchases <span className="text-meta font-medium text-muted-foreground tabular-nums">{purchasing.orders.length}</span></h2>
          <div className="flex flex-wrap items-center gap-snug">
            {ordersOn && <select
              className="h-9 px-3 border rounded-md bg-card text-meta font-semibold"
              value={purchasing.kind}
              onChange={e => { purchasing.setKind(e.target.value); setSelectedId(null); }}
              aria-label="Kind"
              data-testid="po-kind-filter"
            >
              <option value="">Bills and orders</option>
              <option value="direct">Bills entered</option>
              <option value="order">Purchase orders</option>
            </select>}
            {purchasing.departments.length > 1 && (
              <select
                className="h-9 px-3 border rounded-md bg-card text-meta font-semibold"
                value={purchasing.departmentId}
                onChange={e => { purchasing.setDepartmentId(e.target.value); setSelectedId(null); }}
                aria-label="Department"
                data-testid="po-department-filter"
              >
                <option value="">All departments</option>
                {purchasing.departments.map(d => <option value={d.id} key={d.id}>{d.name}</option>)}
              </select>
            )}
            <label className="text-meta font-medium text-muted-foreground" htmlFor="po-status-filter">Status</label>
            <select
              id="po-status-filter"
              className="h-9 px-3 border rounded-md bg-card text-meta font-semibold focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={purchasing.status}
              onChange={e => { purchasing.setStatus(e.target.value); setSelectedId(null); }}
              data-testid="po-status-filter"
            >
              {PO_STATUSES.map(([value, text]) => <option value={value} key={value || "all"}>{text}</option>)}
            </select>
          </div>
        </header>

        <div className="space-y-snug">
          {purchasing.orders.map((order, index) => {
            const isSelected = selectedId === order.id;
            let statusColor = "bg-muted text-muted-foreground";
            if (order.status === "draft") statusColor = "bg-slate-100 text-slate-700 border-slate-200";
            if (order.status === "placed") statusColor = "bg-info-subtle text-info border-info/40";
            if (order.status === "partially_received") statusColor = "bg-warning-subtle text-warning border-warning/40";
            if (order.status === "received") statusColor = "bg-success-subtle text-success border-success/40";
            if (order.status === "cancelled") statusColor = "bg-danger-subtle text-danger border-danger/40";

            return (
              <article key={order.id} className={`px-base py-snug rounded-lg border-2 transition-colors cursor-pointer flex flex-wrap gap-snug items-center justify-between ${isSelected ? 'border-primary bg-primary/5' : 'bg-card border-border hover:border-primary/30'}`} onClick={() => { setSelectedId(order.id); setActionError(null); }} data-testid={`po-row-${index}`}>
                <div className="flex-1 min-w-[120px]">
                  <strong className="block text-lede font-semibold text-foreground tabular-nums">
                    {order.po_number}
                    {order.kind === "direct" && <span className="ml-snug text-micro font-medium text-info" data-testid={`po-row-${index}-bill`}>bill</span>}
                    {order.attachments?.length > 0 && <span className="ml-snug text-micro text-muted-foreground" title="Bill attached">📎{order.attachments.length}</span>}
                  </strong>
                  <small className="text-micro text-muted-foreground">
                    {order.supplier_name || `Supplier #${order.supplier_id}`}
                    {order.department_name ? ` · ${order.department_name}` : ""}
                    {order.invoice_number ? ` · bill ${order.invoice_number}` : ""}
                  </small>
                </div>
                <div className="flex flex-col items-start gap-hair w-28 shrink-0">
                  <span className={`px-snug rounded-md text-meta font-medium border ${statusColor}`} data-testid={`po-row-${index}-status`}>{label(order.status)}</span>
                  <span className="text-micro text-muted-foreground tabular-nums">{day(order.order_date)}</span>
                </div>
                <div className="flex flex-col items-end gap-hair shrink-0 w-24">
                  <b className={MONEY_LINE} data-testid={`po-row-${index}-total`}>{inr(order.total)}</b>
                  <span className="text-meta font-medium text-primary" data-testid={`po-row-${index}-open-button`}>{isSelected ? "Viewing" : "Open →"}</span>
                </div>
              </article>
            );
          })}
        </div>
        
        {!purchasing.orders.length && purchasing.loading && (
          <LoadingState message="Reading purchase orders…" testId="po-loading" />
        )}
        {!purchasing.orders.length && !purchasing.loading && (
          <EmptyState icon={ShoppingCart} title={ordersOn ? "No orders" : "No bills yet"}
            message={!purchasing.loaded ? "Purchases could not be read from the server."
              : ordersOn ? "No purchase orders match this filter." : "Tap Enter a bill when goods arrive."} testId="po-empty" />
        )}
      </section>

      <aside className="w-full lg:w-1/2 lg:sticky lg:top-0 bg-card border rounded-lg p-loose min-w-0" data-testid="po-detail-panel">
        {selected ? <div className="space-y-loose">
          {/* Casing belongs to CSS, not to the string: "ORDER DETAIL" typed in
              capitals is read out letter by letter by a screen reader and cannot be
              lower-cased by a translation. The label says "Order detail"; the class
              does the shouting. */}
          <div className="border-b pb-base">
            <small className="text-meta font-medium text-muted-foreground">{selected.kind === "direct" ? "Bill entered as bought" : "Order detail"}</small>
            <h2 className="text-title font-semibold text-foreground tabular-nums" data-testid="po-detail-number">{selected.po_number}</h2>
            <p className="text-meta text-muted-foreground mt-hair">
              <span className="text-foreground font-semibold">{selected.supplier_name}</span> · <b className="text-meta" data-testid="po-detail-status">{label(selected.status)}</b>
              <br/>{selected.kind === "direct" ? "bought" : "ordered"} <span className="font-semibold tabular-nums">{day(selected.order_date)}</span>
              {selected.expected_date ? ` · expected ${day(selected.expected_date)}` : ""}
              {selected.department_name ? <><br/>Department <span className="font-semibold" data-testid="po-detail-department">{selected.department_name}</span></> : null}
              {selected.invoice_number ? <><br/>Supplier's bill <span className="font-semibold" data-testid="po-detail-invoice">{selected.invoice_number}</span>{selected.invoice_date ? ` of ${day(selected.invoice_date)}` : ""}</> : null}
            </p>
            {selected.notes && <p className="mt-snug text-body bg-muted/50 p-snug rounded-lg border italic">{selected.notes}</p>}
          </div>

          <div className="space-y-hair" data-testid="po-detail-lines">
            <div className="hidden sm:grid grid-cols-12 gap-snug text-meta font-medium text-muted-foreground px-snug border-b pb-hair">
              <span className="col-span-4">Item</span>
              <span className="col-span-2 text-right">Ordered</span>
              <span className="col-span-2 text-right">Received</span>
              <span className="col-span-2 text-right">Cost</span>
              <span className="col-span-2 text-right">Total</span>
            </div>
            {selected.items.map((line, index) => (
              <div key={line.id} className="grid grid-cols-12 gap-snug items-center py-hair border-b last:border-0 text-body" data-testid={`po-detail-line-${index}`}>
                <span className="col-span-12 sm:col-span-4 font-semibold">{line.item_name}</span>
                <span className="col-span-3 sm:col-span-2 text-meta sm:text-right tabular-nums">
                  <span className="sm:hidden text-meta font-medium text-muted-foreground block">Ord</span>
                  {qty(line.quantity_ordered)} <span className="text-micro opacity-70">{line.unit}</span>
                </span>
                <span className="col-span-3 sm:col-span-2 text-meta sm:text-right tabular-nums">
                  <span className="sm:hidden text-meta font-medium text-muted-foreground block">Rec</span>
                  {qty(line.quantity_received)} <span className="text-micro opacity-70">{line.unit}</span>
                  {Number(line.quantity_returned) > 0 && <span className="block text-micro text-warning" data-testid={`po-detail-line-${index}-returned`}>−{qty(line.quantity_returned)} back</span>}
                </span>
                <span className={`col-span-3 sm:col-span-2 sm:text-right ${MONEY_CAPTION}`}>
                  <span className="sm:hidden text-meta font-medium text-muted-foreground block">Cost</span>
                  {inr(line.unit_cost)}
                </span>
                <span className={`col-span-3 sm:col-span-2 text-right ${MONEY_LINE}`}>
                  <span className="sm:hidden text-meta font-medium text-muted-foreground block">Total</span>
                  {inr(line.line_total)}
                </span>
              </div>
            ))}
          </div>

          <div className="bg-muted/30 rounded-lg px-base py-snug border" data-testid="po-detail-totals">
            <div className="flex justify-between items-center gap-snug py-hair">
              <small className="text-meta text-muted-foreground">Subtotal</small>
              <strong className={MONEY_LINE}>{inr(selected.subtotal)}</strong>
            </div>
            <div className="flex justify-between items-center gap-snug py-hair">
              <small className="text-meta text-muted-foreground">Tax</small>
              <strong className={MONEY_LINE}>{inr(selected.tax_amount)}</strong>
            </div>
            <div className="flex justify-between items-center gap-snug py-hair mt-hair border-t border-border/50">
              <small className="text-meta font-medium">Total</small>
              <strong className={`${MONEY_GRAND_TOTAL} text-primary`} data-testid="po-detail-total">{inr(selected.total)}</strong>
            </div>
            <div className="flex justify-between items-center gap-snug py-hair opacity-50">
              <small className="text-meta font-medium">Version</small>
              <strong className="text-micro tabular-nums">{selected.version}</strong>
            </div>
          </div>

          <BillFiles
            key={`files-${selected.id}`}
            files={selected.attachments || []}
            parent={{ orderId: selected.id }}
            token={token}
            canEdit={canEdit}
            onUpload={purchasing.uploadAttachment}
            onDelete={purchasing.deleteAttachment}
            testId="po-files"
          />

          {actionError && <p className="text-body font-semibold text-destructive p-snug bg-destructive/10 rounded-lg" data-testid="po-action-error">{actionError}</p>}

          {canEdit && <div className="flex flex-col sm:flex-row flex-wrap gap-snug pt-hair">
            {selected.status === "draft" && (
              <Button variant="outline" className="flex-1 font-semibold" onClick={() => { setActionError(null); setDialog({ kind: "edit" }); }} data-testid="edit-order-button">
                <Pencil className="w-4 h-4 mr-2"/>Edit draft
              </Button>
            )}
            {["received", "partially_received"].includes(selected.status) && (
              <Button variant="outline" className="flex-1 font-semibold" onClick={() => { setActionError(null); setDialog({ kind: "return" }); }} data-testid="return-goods-button">
                <Undo2 className="w-4 h-4 mr-2"/>Return goods
              </Button>
            )}
            {selected.status === "draft" && (
              <Button variant="action"
                className="flex-1 font-semibold"
                onClick={() => act(() => purchasing.placeOrder(selected.id, selected.version), "sent to the supplier")}
                data-testid="place-order-button"
              >Place order</Button>
            )}
            {["placed", "partially_received"].includes(selected.status) && (
              <Button variant="action" className="flex-1 font-semibold" onClick={() => { setActionError(null); setDialog({ kind: "receive" }); }} data-testid="receive-order-button">
                <PackageCheck className="w-4 h-4 mr-2"/>Receive Delivery
              </Button>
            )}
            {["draft", "placed"].includes(selected.status) && (
              <Button
                variant="destructive"
                className="flex-1 font-semibold"
                onClick={() => act(() => purchasing.cancelOrder(selected.id, selected.version))}
                data-testid="cancel-order-button"
              >Cancel order</Button>
            )}
            {["received", "partially_received"].includes(selected.status) && (
              <Button variant="outline" className="flex-1 font-semibold text-destructive" data-testid="void-bill-button"
                onClick={() => {
                  // Owner brief B6: a wrong bill is voided (its stock goes back out,
                  // it stops being owed), then entered again correctly.
                  const why = window.prompt(`Why void ${selected.invoice_number || selected.po_number}? Its stock goes back out and it stops being owed. Then enter it again correctly.`);
                  if (why && why.trim()) act(() => purchasing.voidBill(selected.id, why.trim(), selected.version), "voided");
                }}>Void this bill</Button>
            )}
          </div>}
          
          {selected.status === "draft" && (
            <p className="text-meta text-muted-foreground text-center" data-testid="place-order-note">
              Placing sends the order to the supplier. No stock moves until the goods are received.
            </p>
          )}
        </div> : (
          /* py-24 (96px) wrapped around a hand-rolled empty state with a 64px
             glyph, on the half of the screen that has the least to show. The
             primitive owns its padding and sizes its own icon by density. */
          <EmptyState
            icon={ShoppingCart}
            title="Open an order"
            message="Select an order to see its lines, totals and actions."
            testId="po-detail-empty"
          />
        )}
      </aside>
    </div>}

    {tab === "suppliers" && <div className="space-y-base" data-testid="suppliers-panel">
      <div>
        <h3 className="text-title font-semibold text-foreground">Suppliers</h3>
        <p className="text-meta text-muted-foreground">{purchasing.suppliers.length} on file</p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-snug">
        {purchasing.suppliers.map((supplier, index) => (
          <article key={supplier.id} className="bg-card border rounded-lg px-base py-snug shadow-sm flex flex-col gap-snug h-full" data-testid={`supplier-${index}`}>
            <header className="flex gap-snug items-center min-w-0">
              <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-micro shrink-0">
                {initials(supplier.name)}
              </span>
              <div className="flex-1 min-w-0">
                <strong className="block text-lede font-semibold text-foreground truncate">{supplier.name}</strong>
                <small className="text-micro text-muted-foreground block truncate">{supplier.gstin || supplier.phone || "no GSTIN on file"}</small>
              </div>
              <span className={`w-2 h-2 rounded-full shrink-0 ${supplier.is_active === false ? "bg-danger" : "bg-success"}`}></span>
            </header>
            <div className="mt-auto flex items-center justify-between gap-snug">
              <span className="text-micro text-muted-foreground">
                <span className="font-bold">Terms</span> <span className="tabular-nums text-foreground">{supplier.payment_terms_days}</span> days
              </span>
              <span className="flex gap-tight">
                {canEdit && !deptLimited && <Button variant="ghost" size="sm" className="h-8 px-snug text-meta font-bold" onClick={() => setDialog({ kind: "vendor-items", supplier })} data-testid={`supplier-${index}-items-button`}>Items</Button>}
                {!deptLimited && <Button variant="ghost" size="sm" className="h-8 px-snug text-meta font-bold" onClick={() => setDialog({ kind: "ledger", supplier })} data-testid={`supplier-${index}-ledger-button`}>Statement</Button>}
                {canEdit && <Button variant="ghost" size="sm" className="h-8 px-snug text-meta font-bold" onClick={() => setDialog({ kind: "supplier", supplier })} data-testid={`supplier-${index}-edit-button`}>Edit</Button>}
              </span>
            </div>
          </article>
        ))}
      </div>
      
      {!purchasing.suppliers.length && purchasing.loading && (
        <LoadingState message="Reading suppliers…" testId="suppliers-loading" />
      )}
      {!purchasing.suppliers.length && !purchasing.loading && (
        <EmptyState icon={Users} title="No suppliers" message="No suppliers on file. Add one before raising a purchase order." testId="suppliers-empty" />
      )}
    </div>}

    {tab === "payables" && <div className="space-y-loose" data-testid="payables-panel">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-snug bg-muted/30 px-base py-snug rounded-lg border">
        <div>
          <h3 className="text-title font-semibold text-foreground">Payables</h3>
          <p className="text-meta text-muted-foreground">Billed is what actually arrived — a placed order is a commitment, not a debt. Returns come off.</p>
        </div>
        {canEdit && <Button variant="action" size="sm" className="font-bold shadow-sm whitespace-nowrap shrink-0" onClick={() => setDialog({ kind: "payment" })} data-testid="record-payment-button">
          <Banknote className="w-4 h-4 mr-snug"/>Record payment
        </Button>}
      </div>

      {purchasing.payables.length > 0 && (
        <div className="rounded-xl border bg-card overflow-hidden shadow-sm" data-testid="payables-table">
          <div className="overflow-x-auto">
            <table className="w-full text-body text-left">
              <thead className="text-meta text-muted-foreground bg-muted/50">
                <tr>
                  <th className="px-base py-snug font-medium">Supplier</th>
                  <th className="px-base py-snug font-medium text-right">Terms</th>
                  <th className="px-base py-snug font-medium text-right">Billed</th>
                  <th className="px-base py-snug font-medium text-right">Returned</th>
                  <th className="px-base py-snug font-medium text-right">Paid</th>
                  <th className="px-base py-snug font-medium text-right">Outstanding</th>
                  <th className="px-base py-snug font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {purchasing.payables.map((row, index) => (
                  <tr key={row.supplier_id} className="hover:bg-muted/30 transition-colors" data-testid={`payable-${index}`}>
                    <td className="px-base py-snug">
                      <div className="flex items-center gap-snug">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${Number(row.outstanding) > 0 ? "bg-warning shadow-[0_0_8px_rgba(245,158,11,0.6)]" : "bg-success"}`}></div>
                        <strong className="font-semibold text-foreground">{row.supplier_name}</strong>
                      </div>
                    </td>
                    <td className="px-base py-snug text-right text-muted-foreground tabular-nums">{row.payment_terms_days} <span className="text-meta ml-hair">days</span></td>
                    <td className={`px-base py-snug text-right ${MONEY_LINE}`} data-testid={`payable-${index}-billed`}>{inr(row.billed)}</td>
                    <td className={`px-base py-snug text-right ${MONEY_LINE}`} data-testid={`payable-${index}-returned`}>{row.returned ? inr(row.returned) : "—"}</td>
                    <td className={`px-base py-snug text-right ${MONEY_LINE} text-success`} data-testid={`payable-${index}-paid`}>{inr(row.paid)}</td>
                    <td className="px-base py-snug text-right">
                      <span className={`${MONEY_LINE} font-bold ${Number(row.outstanding) > 0 ? "text-warning" : "text-success"}`} data-testid={`payable-${index}-outstanding`}>
                        {inr(row.outstanding)}
                      </span>
                    </td>
                    <td className="px-base py-snug text-right">
                      {canEdit && <Button variant="outline" size="sm" className="h-8 text-meta font-bold shadow-sm border-primary/20 hover:bg-primary/5 text-primary" onClick={() => setDialog({ kind: "payment", supplierId: row.supplier_id })} data-testid={`payable-${index}-pay-button`}>Pay</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!purchasing.payables.length && purchasing.loading && (
        <LoadingState message="Reading payables…" testId="payables-loading" />
      )}
      {!purchasing.payables.length && !purchasing.loading && (
        <EmptyState icon={CheckCircle2} title="All Settled" message="Nothing is owed — no supplier has a received order on file." testId="payables-empty" />
      )}

      <div>
        <h3 className="text-title font-semibold text-foreground mb-snug">Recent payments</h3>
        {/* Two lines and one padding step. The 48px date medallion was a decorative
            restatement of a date the meta line can carry in eight characters. */}
        <div className="border rounded-lg bg-card divide-y overflow-hidden">
          {purchasing.payments.map((payment, index) => (
            <div className="flex items-baseline justify-between gap-snug px-base py-snug" key={payment.id} data-testid={`payment-${index}`}>
              <div className="flex flex-col min-w-0">
                <strong className="text-body font-semibold text-foreground truncate">{purchasing.suppliers.find(s => s.id === payment.supplier_id)?.name || `Supplier #${payment.supplier_id}`}</strong>
                <small className="text-micro text-muted-foreground truncate">
                  <span className="tabular-nums">{day(payment.paid_on)}</span> · <span className="capitalize">{label(payment.mode)}</span>
                  {payment.paid_from ? ` · ${payment.paid_from}` : ""}
                  {payment.is_advance ? " · advance" : ""}
                  {payment.purchase_order_id ? ` · order #${payment.purchase_order_id}` : " · on account"}
                  {payment.notes ? ` · ${payment.notes}` : ""}
                  {payment.voided_at ? ` · VOID: ${payment.void_reason}` : ""}
                </small>
              </div>
              <div className="flex items-center gap-snug shrink-0">
                <b className={`${MONEY_LINE} ${payment.voided_at ? "line-through text-muted-foreground" : "text-success"}`}>{inr(payment.amount)}</b>
                {canEdit && !payment.voided_at && (
                  <Button variant="ghost" size="sm" className="h-11" data-testid={`payment-${index}-void`}
                          onClick={async () => {
                            const why = window.prompt(`Why void the ${inr(payment.amount)} payment? It stays on record, marked void.`);
                            if (!why || !why.trim()) return;
                            const r = await purchasing.voidPayment(payment.id, why.trim());
                            r.ok ? toast.success(`${inr(payment.amount)} payment voided`) : toast.error(`Not voided — ${r.error}`);
                          }}>Void</Button>
                )}
              </div>
            </div>
          ))}
        </div>
        {!purchasing.payments.length && !purchasing.loading && (
          <EmptyState density="inline" icon={Banknote} title="No payments" message="No supplier payments recorded yet." testId="payments-empty" />
        )}
      </div>
    </div>}

    {tab === "returns" && <div className="space-y-base" data-testid="returns-panel">
      <p className="text-meta text-muted-foreground">Goods sent back. Start one from a purchase with "Return goods"; stock goes out and the supplier is owed less.</p>
      {purchasing.returns.map((ret, index) => (
        <article key={ret.id} className="bg-card border rounded-lg px-base py-snug space-y-snug" data-testid={`return-${index}`}>
          <header className="flex flex-wrap items-baseline justify-between gap-snug">
            <span>
              <strong className="text-lede font-semibold tabular-nums">{ret.return_number}</strong>
              <small className="text-micro text-muted-foreground"> · {ret.supplier_name}{ret.po_number ? ` · from ${ret.po_number}` : ""}{ret.department_name ? ` · ${ret.department_name}` : ""} · {day(ret.return_date)}</small>
            </span>
            <b className={MONEY_LINE}>{inr(ret.total)}</b>
          </header>
          <p className="text-meta text-muted-foreground">
            {ret.items.map(i => `${i.item_name} ${qty(i.quantity)} ${i.unit}`).join(" · ")}{ret.reason ? ` — ${ret.reason}` : ""}
          </p>
          <BillFiles files={ret.attachments || []} parent={{ returnId: ret.id }} token={token} canEdit={canEdit}
                     onUpload={purchasing.uploadAttachment} onDelete={purchasing.deleteAttachment} testId={`return-${index}-files`} />
        </article>
      ))}
      {!purchasing.returns.length && !purchasing.loading && (
        <EmptyState icon={Undo2} title="No returns" message="Nothing has been sent back to a supplier." testId="returns-empty" />
      )}
    </div>}

    {tab === "reports" && <PurchaseReports token={token} />}

    {tab === "departments" && (
      <Departments departments={purchasing.departments} canManage={canEdit && !deptLimited}
                   onAdd={purchasing.addDepartment} onEdit={purchasing.editDepartment} />
    )}

    {(dialog?.kind === "bill" || (dialog?.kind === "edit" && selected)) && (
      <OrderDialog
        key={dialog.kind === "edit" ? `edit-${selected.id}-${selected.version}` : "bill"}
        mode={dialog.kind === "bill" ? "bill" : "order"}
        sources={sources}
        initialPaidFrom={dialog.paidFrom || ""}
        order={dialog.kind === "edit" ? selected : null}
        suppliers={purchasing.suppliers}
        stockItems={purchasing.stockItems}
        departments={purchasing.departments}
        onSave={dialog.kind === "bill" ? purchasing.addDirect : body => purchasing.editOrder(selected.id, body, selected.version)}
        onUpload={purchasing.uploadAttachment}
        onClose={() => setDialog(null)}
      />
    )}
    {dialog?.kind === "return" && selected && (
      <ReturnDialog order={selected} onSave={purchasing.addReturn} onClose={() => setDialog(null)} />
    )}
    {dialog?.kind === "vendor-items" && (
      <VendorItems supplier={dialog.supplier} stockItems={purchasing.stockItems} token={token} onClose={() => setDialog(null)} />
    )}
    {dialog?.kind === "ledger" && (
      <SupplierLedger supplier={dialog.supplier} token={token} onClose={() => setDialog(null)} />
    )}
    {dialog?.kind === "supplier" && (
      <SupplierDialog
        supplier={dialog.supplier}
        onSave={body => (dialog.supplier ? purchasing.editSupplier(dialog.supplier.id, body) : purchasing.addSupplier(body))}
        onClose={() => setDialog(null)}
      />
    )}
    {dialog?.kind === "order" && (
      <OrderDialog
        suppliers={purchasing.suppliers}
        stockItems={purchasing.stockItems}
        departments={purchasing.departments}
        onSave={purchasing.addOrder}
        onUpload={purchasing.uploadAttachment}
        onClose={() => setDialog(null)}
      />
    )}
    {dialog?.kind === "receive" && selected && (
      <ReceiveDialog
        key={`receive-${selected.id}-${selected.version}`}
        order={selected}
        onReceive={purchasing.receiveOrder}
        onClose={() => setDialog(null)}
      />
    )}
    {dialog?.kind === "payment" && (
      <PaymentDialog
        sources={sources}
        suppliers={purchasing.suppliers}
        orders={purchasing.orders}
        presetSupplierId={dialog.supplierId}
        onSave={purchasing.addPayment}
        onClose={() => setDialog(null)}
      />
    )}
  </PageShell>;
}
